import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent, WheelEvent } from "react";
import * as THREE from "three";
import { renderStore } from "../store/renderStore";
import {
  ExportFormatId,
  ExportFormatOption,
  ResolvedExportScale,
  ScalePreset,
  clampExportZoom,
  computeExportCaptureMetrics,
  getDrawablePixelSize,
  getExportFormatOption,
  getPreviewSize,
  getTargetZoomForScale,
  getTargetZoomForScaleDenominator,
  resolveExportScale,
} from "../utils/exportFormats";

interface UseExportPreviewStateOptions {
  defaultFormatId: ExportFormatId;
  maxPreviewDimension?: number;
}

export const useExportPreviewState = ({
  defaultFormatId,
  maxPreviewDimension = 400,
}: UseExportPreviewStateOptions) => {
  type PreviousPreviewState = {
    formatId: ExportFormatId;
    scalePreset: ScalePreset;
    scaleDenominator: number | null;
    captureCenter: { x: number; y: number } | null;
  };

  const [selectedFormatId, setSelectedFormatId] = useState<ExportFormatId>(defaultFormatId);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [sceneBox, setSceneBox] = useState<THREE.Box3 | null>(null);
  const [selectedScalePreset, setSelectedScalePreset] = useState<ScalePreset>("custom");

  useEffect(() => {
    if (!renderStore.scene) {
      return;
    }

    const nextSceneBox = renderStore.computeBoundingBoxForScene(renderStore.scene);
    setSceneBox(nextSceneBox.isEmpty() ? null : nextSceneBox.clone());
  }, []);

  const selectedFormat = useMemo<ExportFormatOption>(
    () => getExportFormatOption(selectedFormatId),
    [selectedFormatId],
  );

  const previewSize = useMemo(
    () => getPreviewSize(selectedFormat, maxPreviewDimension),
    [maxPreviewDimension, selectedFormat],
  );

  const drawingPreviewSize = useMemo(() => {
    return previewSize;
  }, [previewSize]);

  const drawablePixelSize = useMemo(
    () => getDrawablePixelSize(selectedFormat),
    [selectedFormat],
  );

  const captureMetrics = useMemo(() => {
    if (!sceneBox) {
      return null;
    }

    // Pan values are in preview pixels, but computeExportCaptureMetrics expects
    // values in output-pixel space. Scale by the ratio of output to preview size.
    const scaledPan = {
      x: pan.x * (drawablePixelSize.width / previewSize.width),
      y: pan.y * (drawablePixelSize.height / previewSize.height),
    };

    return computeExportCaptureMetrics(sceneBox, drawablePixelSize, zoom, scaledPan);
  }, [drawablePixelSize, pan, previewSize, sceneBox, zoom]);

  const resolvedScale = useMemo<ResolvedExportScale>(() => {
    if (!captureMetrics) {
      return resolveExportScale(selectedFormat, 0, 0);
    }

    return resolveExportScale(
      selectedFormat,
      captureMetrics.captureWidthWorld,
      captureMetrics.captureHeightWorld,
    );
  }, [captureMetrics, selectedFormat]);

  const handleWheelZoom = (event: WheelEvent) => {
    const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
    if (selectedFormat.supportsScale) {
      setSelectedScalePreset("custom");
    }
    setZoom((previousZoom) => clampExportZoom(previousZoom * zoomFactor));
  };

  const handleZoomStep = (delta: number) => {
    if (selectedFormat.supportsScale) {
      setSelectedScalePreset("custom");
    }
    setZoom((previousZoom) => clampExportZoom(previousZoom + delta));
  };

  const handlePan = (event: MouseEvent) => {
    if (event.buttons !== 1) {
      return;
    }

    setPan((previousPan) => ({
      x: previousPan.x + event.movementX,
      y: previousPan.y + event.movementY,
    }));
  };

  const setScalePreset = (scalePreset: ScalePreset) => {
    setSelectedScalePreset(scalePreset);

    if (scalePreset === "custom" || !sceneBox || !selectedFormat.supportsScale) {
      return;
    }

    const targetZoom = getTargetZoomForScale(sceneBox, selectedFormat, drawablePixelSize, scalePreset);
    if (targetZoom !== null) {
      setZoom(targetZoom);
    }
  };

  const setFormatId = (formatId: ExportFormatId) => {
    setSelectedFormatId(formatId);
  };

  const previousPreviewStateRef = useRef<PreviousPreviewState | null>(null);

  /** Non-paper formats hide scale; reset the dropdown to Custom for those. */
  useEffect(() => {
    if (!selectedFormat.supportsScale) {
      setSelectedScalePreset("custom");
    }
  }, [selectedFormat.supportsScale]);

  /**
   * Preserve the current real-world scale when the paper size changes.
   * Without this, switching A4 -> A3 keeps the old crop and silently changes
   * the resolved scale instead of showing more of the plan.
   */
  useEffect(() => {
    const previousPreviewState = previousPreviewStateRef.current;
    const formatJustChanged =
      previousPreviewState !== null && previousPreviewState.formatId !== selectedFormatId;

    if (!formatJustChanged || !previousPreviewState) {
      return;
    }

    if (!selectedFormat.supportsScale || !sceneBox) {
      return;
    }

    const targetZoom =
      previousPreviewState.scalePreset !== "custom"
        ? getTargetZoomForScale(sceneBox, selectedFormat, drawablePixelSize, previousPreviewState.scalePreset)
        : previousPreviewState.scaleDenominator === null
          ? null
          : getTargetZoomForScaleDenominator(
              sceneBox,
              selectedFormat,
              drawablePixelSize,
              previousPreviewState.scaleDenominator,
            );

    if (targetZoom !== null) {
      setZoom(targetZoom);

      if (previousPreviewState.captureCenter) {
        const centeredCaptureMetrics = computeExportCaptureMetrics(sceneBox, drawablePixelSize, targetZoom, {
          x: 0,
          y: 0,
        });

        if (centeredCaptureMetrics) {
          const panScaleX = drawablePixelSize.width / previewSize.width;
          const panScaleY = drawablePixelSize.height / previewSize.height;
          const worldOffsetX = previousPreviewState.captureCenter.x - centeredCaptureMetrics.sceneCenter.x;
          const worldOffsetY = previousPreviewState.captureCenter.y - centeredCaptureMetrics.sceneCenter.y;

          setPan({
            x: -worldOffsetX / (centeredCaptureMetrics.unitsPerPixel * panScaleX),
            y: worldOffsetY / (centeredCaptureMetrics.unitsPerPixel * panScaleY),
          });
        }
      }
    }
  }, [drawablePixelSize, previewSize.height, previewSize.width, sceneBox, selectedFormat, selectedFormatId]);

  useEffect(() => {
    const captureCenter = captureMetrics?.captureBox.getCenter(new THREE.Vector3()) ?? null;

    previousPreviewStateRef.current = {
      formatId: selectedFormatId,
      scalePreset: selectedScalePreset,
      scaleDenominator: resolvedScale.scaleDenominator,
      captureCenter: captureCenter
        ? {
            x: captureCenter.x,
            y: captureCenter.y,
          }
        : null,
    };
  }, [captureMetrics, resolvedScale.scaleDenominator, selectedFormatId, selectedScalePreset]);

  return {
    selectedFormatId,
    selectedFormat,
    setFormatId,
    zoom,
    pan,
    previewSize,
    drawingPreviewSize,
    drawablePixelSize,
    captureMetrics,
    resolvedScale,
    selectedScalePreset,
    handleWheelZoom,
    handleZoomStep,
    handlePan,
    setScalePreset,
    setPan,
    setZoom,
  };
};
